// shared-components/components/FormInputs.jsx
import React, { useState } from 'react';

/**
 * Unified Form Input Components
 * 
 * Provides consistent form controls with proper states, accessibility, and design tokens.
 * All components support: default, error, disabled, and focus states.
 */

/**
 * Input - Text input component
 * 
 * @param {string} value - Input value
 * @param {Function} onChange - Change handler
 * @param {string} placeholder - Placeholder text
 * @param {string} type - Input type: 'text', 'email', 'password', 'number', etc.
 * @param {string} label - Input label
 * @param {string} error - Error message
 * @param {boolean} disabled - Disabled state
 * @param {boolean} required - Required field
 * @param {string} size - Input size: 'small', 'medium', 'large'
 * @param {object} style - Additional inline styles
 */
export function Input({ 
  value, 
  onChange, 
  placeholder,
  type = 'text',
  label,
  error,
  disabled,
  required,
  size = 'medium',
  className,
  style,
  ...props 
}) {
  const [isFocused, setIsFocused] = useState(false);

  const sizeStyles = {
    small: {
      padding: 'var(--space-2) var(--space-3)',
      fontSize: 'var(--text-sm)'
    },
    medium: {
      padding: 'var(--space-3) var(--space-4)',
      fontSize: 'var(--text-base)'
    },
    large: {
      padding: 'var(--space-4) var(--space-5)',
      fontSize: 'var(--text-lg)'
    }
  };

  const inputStyles = {
    ...sizeStyles[size],
    width: '100%',
    fontFamily: 'inherit',
    color: disabled ? 'var(--text-disabled)' : 'var(--text-primary)',
    background: disabled ? 'var(--bg-tertiary)' : 'var(--bg-primary)',
    border: `1px solid ${error ? 'var(--error)' : isFocused ? 'var(--compudopt-green)' : 'var(--border-default)'}`,
    borderRadius: 'var(--radius-sm)',
    outline: 'none',
    transition: 'all var(--duration-fast) var(--ease-out)',
    cursor: disabled ? 'not-allowed' : 'text',
    ...style
  };

  return (
    <div style={{ marginBottom: 'var(--space-4)' }}>
      {label && (
        <label style={{
          display: 'block',
          marginBottom: 'var(--space-2)',
          fontSize: 'var(--text-sm)',
          fontWeight: 'var(--font-medium)',
          color: 'var(--text-secondary)'
        }}>
          {label}
          {required && <span style={{ color: 'var(--error)', marginLeft: 'var(--space-1)' }}>*</span>}
        </label>
      )}
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        className={className}
        style={inputStyles}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        {...props}
      />
      {error && (
        <div style={{
          marginTop: 'var(--space-2)',
          fontSize: 'var(--text-xs)',
          color: 'var(--error)'
        }}>
          {error}
        </div>
      )}
    </div>
  );
}

/**
 * Textarea - Multi-line text input
 * 
 * @param {string} value - Textarea value
 * @param {Function} onChange - Change handler
 * @param {string} placeholder - Placeholder text
 * @param {string} label - Textarea label
 * @param {string} error - Error message
 * @param {boolean} disabled - Disabled state
 * @param {boolean} required - Required field
 * @param {number} rows - Number of visible rows
 * @param {object} style - Additional inline styles
 */
export function Textarea({ 
  value, 
  onChange, 
  placeholder,
  label,
  error,
  disabled,
  required,
  rows = 4,
  className,
  style,
  ...props 
}) {
  const [isFocused, setIsFocused] = useState(false);

  const textareaStyles = {
    width: '100%',
    padding: 'var(--space-3) var(--space-4)',
    fontFamily: 'inherit',
    fontSize: 'var(--text-base)',
    lineHeight: 'var(--leading-normal)',
    color: disabled ? 'var(--text-disabled)' : 'var(--text-primary)',
    background: disabled ? 'var(--bg-tertiary)' : 'var(--bg-primary)',
    border: `1px solid ${error ? 'var(--error)' : isFocused ? 'var(--compudopt-green)' : 'var(--border-default)'}`,
    borderRadius: 'var(--radius-sm)',
    outline: 'none',
    transition: 'all var(--duration-fast) var(--ease-out)',
    cursor: disabled ? 'not-allowed' : 'text',
    resize: 'vertical',
    ...style
  };

  return (
    <div style={{ marginBottom: 'var(--space-4)' }}>
      {label && (
        <label style={{
          display: 'block',
          marginBottom: 'var(--space-2)',
          fontSize: 'var(--text-sm)',
          fontWeight: 'var(--font-medium)',
          color: 'var(--text-secondary)'
        }}>
          {label}
          {required && <span style={{ color: 'var(--error)', marginLeft: 'var(--space-1)' }}>*</span>}
        </label>
      )}
      <textarea
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        rows={rows}
        className={className}
        style={textareaStyles}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        {...props}
      />
      {error && (
        <div style={{
          marginTop: 'var(--space-2)',
          fontSize: 'var(--text-xs)',
          color: 'var(--error)'
        }}>
          {error}
        </div>
      )}
    </div>
  );
}

/**
 * Select - Dropdown select component
 * 
 * @param {string} value - Selected value
 * @param {Function} onChange - Change handler
 * @param {Array} options - Options array: [{ value, label }, ...]
 * @param {string} placeholder - Placeholder text
 * @param {string} label - Select label
 * @param {string} error - Error message
 * @param {boolean} disabled - Disabled state
 * @param {boolean} required - Required field
 * @param {string} size - Select size: 'small', 'medium', 'large'
 * @param {object} style - Additional inline styles
 */
export function Select({ 
  value, 
  onChange, 
  options = [],
  placeholder,
  label,
  error,
  disabled,
  required,
  size = 'medium',
  className,
  style,
  ...props 
}) {
  const [isFocused, setIsFocused] = useState(false);

  const sizeStyles = {
    small: {
      padding: 'var(--space-2) var(--space-3)',
      fontSize: 'var(--text-sm)'
    },
    medium: {
      padding: 'var(--space-3) var(--space-4)',
      fontSize: 'var(--text-base)'
    },
    large: {
      padding: 'var(--space-4) var(--space-5)',
      fontSize: 'var(--text-lg)'
    }
  };

  const selectStyles = {
    ...sizeStyles[size],
    width: '100%',
    fontFamily: 'inherit',
    color: disabled ? 'var(--text-disabled)' : value ? 'var(--text-primary)' : 'var(--text-tertiary)',
    background: disabled ? 'var(--bg-tertiary)' : 'var(--bg-primary)',
    border: `1px solid ${error ? 'var(--error)' : isFocused ? 'var(--compudopt-green)' : 'var(--border-default)'}`,
    borderRadius: 'var(--radius-sm)',
    outline: 'none',
    transition: 'all var(--duration-fast) var(--ease-out)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right var(--space-3) center',
    backgroundSize: '1em',
    paddingRight: 'var(--space-8)',
    ...style
  };

  return (
    <div style={{ marginBottom: 'var(--space-4)' }}>
      {label && (
        <label style={{
          display: 'block',
          marginBottom: 'var(--space-2)',
          fontSize: 'var(--text-sm)',
          fontWeight: 'var(--font-medium)',
          color: 'var(--text-secondary)'
        }}>
          {label}
          {required && <span style={{ color: 'var(--error)', marginLeft: 'var(--space-1)' }}>*</span>}
        </label>
      )}
      <select
        value={value}
        onChange={onChange}
        disabled={disabled}
        required={required}
        className={className}
        style={selectStyles}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        {...props}
      >
        {placeholder && (
          <option value="" disabled hidden>
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {error && (
        <div style={{
          marginTop: 'var(--space-2)',
          fontSize: 'var(--text-xs)',
          color: 'var(--error)'
        }}>
          {error}
        </div>
      )}
    </div>
  );
}

/**
 * Checkbox - Checkbox input with label
 * 
 * @param {boolean} checked - Checked state
 * @param {Function} onChange - Change handler
 * @param {string} label - Checkbox label
 * @param {boolean} disabled - Disabled state
 * @param {object} style - Additional inline styles
 */
export function Checkbox({ 
  checked, 
  onChange, 
  label,
  disabled,
  className,
  style,
  ...props 
}) {
  return (
    <label style={{
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--space-2)',
      cursor: disabled ? 'not-allowed' : 'pointer',
      fontSize: 'var(--text-base)',
      color: disabled ? 'var(--text-disabled)' : 'var(--text-primary)',
      userSelect: 'none',
      ...style
    }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className={className}
        style={{
          width: 'var(--space-4)',
          height: 'var(--space-4)',
          cursor: disabled ? 'not-allowed' : 'pointer'
        }}
        {...props}
      />
      {label}
    </label>
  );
}

/**
 * Radio - Radio input with label
 * 
 * @param {boolean} checked - Checked state
 * @param {Function} onChange - Change handler
 * @param {string} name - Radio group name
 * @param {string} value - Radio value
 * @param {string} label - Radio label
 * @param {boolean} disabled - Disabled state
 * @param {object} style - Additional inline styles
 */
export function Radio({ 
  checked, 
  onChange, 
  name,
  value,
  label,
  disabled,
  className,
  style,
  ...props 
}) {
  return (
    <label style={{
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--space-2)',
      cursor: disabled ? 'not-allowed' : 'pointer',
      fontSize: 'var(--text-base)',
      color: disabled ? 'var(--text-disabled)' : 'var(--text-primary)',
      userSelect: 'none',
      ...style
    }}>
      <input
        type="radio"
        checked={checked}
        onChange={onChange}
        name={name}
        value={value}
        disabled={disabled}
        className={className}
        style={{
          width: 'var(--space-4)',
          height: 'var(--space-4)',
          cursor: disabled ? 'not-allowed' : 'pointer'
        }}
        {...props}
      />
      {label}
    </label>
  );
}

// Export all form components
export default {
  Input,
  Textarea,
  Select,
  Checkbox,
  Radio
};


